import { EMPTY, expand, filter, mergeMap, of, takeWhile, tap, timer } from 'rxjs'
import { catchError, switchMap, withLatestFrom } from 'rxjs/operators'
import type { Epic } from '../../../index.js'
import { Actions } from '../../../Slice/index.js'
import { withBlockingRequest } from '../../Application/withBlockingRequest.js'
import { trackReportDownload } from '../../../../Services/Telemetry/index.js'
import { startDownload } from '../../../../Util/startDownload.js'

const POLL_DELAY_MS = 3000 // Wait 3 seconds before retrying if 202 is returned

export const GetReportEpic: Epic = (action$, state$, { order, OrderRepository }) => {
  return action$.pipe(
    filter(Actions.order.getReportRequest.match),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      // Resolve the order (for role attribution) from the store, falling back to just the id.
      const stateOrder = state.order.order
      const orderForRole =
        stateOrder && stateOrder !== 'LOADING' && stateOrder.id === action.payload.orderId ? stateOrder : { id: action.payload.orderId }

      const $op = OrderRepository.downloadReport(action.payload.orderId, action.payload.checksum, action.payload.settings).pipe(
        // The FIRST response decides it: 202 => generation triggered, 200 => served directly.
        // tap sits upstream of expand, so it only sees the original response, not the poll retries.
        tap((first) => {
          if (first.status === 202) trackReportDownload('fresh_generation', orderForRole)
          else if (first.status === 200) trackReportDownload('direct', orderForRole)
        }),
        expand((response) =>
          response.status === 202
            ? timer(POLL_DELAY_MS).pipe(switchMap(() => OrderRepository.downloadReport(action.payload.orderId, action.payload.checksum, action.payload.settings)))
            : EMPTY
        ),
        takeWhile((r) => r.status === 202, true),
        switchMap((response) => {
          if (response.status === 200) {
            const s3DownloadUrl = response.data.link
            startDownload(s3DownloadUrl)
            return of(order.getReportRequestSuccess({ orderId: action.payload.orderId, link: s3DownloadUrl }))
          }
          if (response.status === 202) {
            // unreachable because takeWhile stops at first non-202 after expanding, but kept for clarity
            return EMPTY
          }
          return of(order.getReportRequestFailure({ error: new Error(`Unexpected response status: ${response.status}`) }))
        }),
        catchError((err) => of(order.getReportRequestFailure({ error: err })))
      )
      return withBlockingRequest(
        $op,
        'Generating Fresh Report...',
        'Data for report has changed and a new report is being generated. This operation may take longer (up to 1 hour) depending on the number of samples. You may wait or close this window and fetch your report later.'
      )
    })
  )
}
